/**
 * Enquête n°21 — Adapté de l'Étude de Cas CCS n°16
 * (Influences liées à la circulation en groupe lors d'un événement sportif.)
 */

import { Users, AlertTriangle, Lightbulb, Quote, ShieldCheck } from 'lucide-react';

export const groupeEvenementSportifEnquete = {
  slug: 'groupe-evenement-sportif',
  themeCode: 'M',
  issueNumber: 21,
  totalIssues: 21,
  series: 'enquetes',
  category: 'Enquête facteurs sociaux',
  shortTitle: 'En meute après un GP',
  title: 'Bol d’Or, MotoGP : pourquoi le retour de course tue',
  kicker: 'Enquête n°21 · Série « Témoignages & Sécurité »',
  subtitle:
    "Le Bol d’Or rassemble 60 000 motards. Le Grand Prix de France, 100 000. Après 8 heures de course à admirer des pilotes Pro, l'effet de groupe devient piloter au-dessus de soi. Anatomie d'un cocktail explosif.",
  heroImage: '/img/magazines/environnement.jpg',
  heroImageAlt: 'Deux pilotes moto sportifs en virage inclinés à genou — ambiance retour de course (Bol d\'Or, MotoGP)',
  readTimeMin: 6,
  seoDescription:
    'Enquête sur les accidents lors des retours d’événements moto (Bol d’Or, Le Mans, MotoGP) : effet de groupe, surestimation, mimétisme sportif, 5 règles pour rentrer vivant.',
  seoKeywords:
    'Bol d’Or, MotoGP, retour de course, circulation en groupe motard, effet de groupe, mimétisme, accident moto',

  edito: {
    title: 'Le retour de course, danger sous-estimé',
    body: 'Chaque année après le Bol d’Or et le Grand Prix de France, la <strong>Gendarmerie nationale</strong> publie un bilan glaant : <strong>5 à 12 motards décédés</strong> dans les 48 h qui suivent. Pas en course. Sur le trajet du retour. Pourquoi ? Parce que pendant 8 heures, leur cerveau a vu Quartararo à 280 km/h et Razgatlioglu pencher à 65°. Au retour, le cerveau croit qu’il peut tout faire — alors que le pilote, lui, est moyen.',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: Users,
      partLabel: 'Chapitre 1',
      title: 'Les 4 effets du groupe sur ton cerveau',
      paragraphs: [
        '<strong>1. La déresponsabilisation collective</strong> : tu suis le rythme du leader, tu cesses de penser par toi-même. Si le leader prend un virage à 110 sans visibilité, tu suis. Ce n’est plus toi qui décides.',
        '<strong>2. La surestimation par mimétisme</strong> : tu viens de voir des pros à fond, ton cerveau croit que tu peux le faire aussi. Or, sur la route, tu n’as ni piste sécurisée, ni commissaires, ni service médical à 30 secondes.',
        "<strong>3. La pression du peer pressure</strong> : tu ne veux pas être celui qui ralentit. Tu ne veux pas casser l'allure. Résultat : tu pilotes au-dessus de ton niveau pour ne pas « perdre la face » devant les copains.",
        '<strong>4. La fatigue cachée</strong> : 8 h dans le bruit, le soleil, parfois la bière. Ton temps de réaction est doublé. Et tu ne le sens pas.',
      ],
      keyPoints: [
        'Déresponsabilisation collective',
        'Mimétisme sportif dangereux',
        'Peer pressure = vitesse',
        'Fatigue invisible',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Une statistique qui devrait alerter',
      body: "Selon une étude de la <strong>Mutuelle des Motards</strong> (2019), le risque d’accident est multiplié par <strong>3,5</strong> dans les 48 h qui suivent un grand rassemblement sportif moto. La fatigue + l'effet de groupe + le mimétisme sportif créent un cocktail unique. Plus dangereux qu’un week-end normal. Pourquoi ? Parce que ton cerveau a l'illusion d’avoir vu 8 h de pilotage à ton niveau. Faux. Tu as vu les meilleurs au monde.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les 5 règles pour rentrer vivant',
      paragraphs: [
        "<strong>1. Roule à TON niveau</strong>, jamais à celui des autres. Si le leader force, sors du groupe à la station-service suivante.",
        '<strong>2. Pauses obligatoires</strong> toutes les heures, même si tu « te sens bien ». La fatigue est invisible.',
        '<strong>3. Espacement de sécurité</strong> entre motards : 2 secondes minimum, 3 si la route est sinueuse. Pas de « train » collé.',
        '<strong>4. Zéro alcool</strong> au retour. Même une seule bière multiplie le risque par 3.',
        '<strong>5. Briefing AVANT le départ</strong> : qui ouvre, qui ferme, vitesse cible, points de regroupement. Pas d’improvisation.',
      ],
      keyPoints: [
        'Ton niveau, pas celui des autres',
        'Pause toutes les heures',
        '2 à 3 s entre motards',
        '0,0 g/l',
        'Briefing avant départ',
      ],
},
    {
      type: 'tip',
      icon: Users,
      title: 'Tu vas au prochain Bol d’Or ?',
      body: 'Partage cette enquête au club AVANT. Mieux : organise un briefing collectif au camping. <strong>5 minutes pour parler du retour</strong> peuvent sauver une vie ce week-end-là.',
    }
  ],
};

export default groupeEvenementSportifEnquete;
